/**
 * Occasion-first navigation (home → browse → product → customize → checkout → share).
 * `giftkartCategoryId` is used when saving to RTDB `giftkart/{id}/{giftKey}` for GiftDetails page.
 */
export const JOURNEY_CATEGORIES = [
  {
    slug: "egift",
    label: "E-gift cards",
    emoji: "🎁",
    description: "All-purpose digital cards for any moment.",
    giftkartCategoryId: "birthday",
    match: () => true,
  },
  {
    slug: "anniversary",
    label: "Anniversary E-Gift Card",
    emoji: "💑",
    description: "Celebrate years together with a heartfelt card.",
    giftkartCategoryId: "marriageanniversary",
    match: (g) =>
      matches(g, [
        "anniversary",
        "marriage",
        "wedding anniversary",
        "years together",
      ]),
  },
  {
    slug: "birthday",
    label: "Birthday E-Gift Card",
    emoji: "🎂",
    description: "Make their day with confetti, color, and warmth.",
    giftkartCategoryId: "birthday",
    match: (g) => matches(g, ["birthday", "bday", "cake", "turning"]),
  },
  {
    slug: "invitation",
    label: "Invitation",
    emoji: "💌",
    description: "Wedding, party, or any celebration — invite in style.",
    giftkartCategoryId: "weddinginvitation",
    match: (g) =>
      matches(g, [
        "invitation",
        "invite",
        "wedding",
        "party",
        "rsvp",
        "ceremony",
      ]),
  },
  {
    slug: "proposal",
    label: "Proposal",
    emoji: "💍",
    description: "Pop the question with a card they will never forget.",
    giftkartCategoryId: "valentine",
    match: (g) =>
      matches(g, ["proposal", "propose", "engagement", "ring", "love"]),
  },
  {
    slug: "missing-message",
    label: "Missing message",
    emoji: "💬",
    description: "Say sorry, I miss you, or thinking of you — gently and honestly.",
    giftkartCategoryId: "birthday",
    match: (g) =>
      matches(g, [
        "miss",
        "sorry",
        "thinking of you",
        "apology",
        "distance",
      ]),
  },
];

function matches(product, keywords) {
  const blob = `${product.Tittle || ""} ${product.Description || ""} ${
    product.Category || product.category || product.Type || ""
  }`.toLowerCase();
  return keywords.some((k) => blob.includes(k));
}

export function getJourneyBySlug(slug) {
  return JOURNEY_CATEGORIES.find((c) => c.slug === slug) || JOURNEY_CATEGORIES[0];
}

export function filterGiftsByJourneySlug(gifts, slug) {
  const cfg = getJourneyBySlug(slug);
  if (!cfg || cfg.slug === "egift") return gifts;
  return gifts.filter((g) => cfg.match({ ...g }));
}

/**
 * Whether a catalog summary (from `gifts/`) belongs on `/browse/:slug`.
 * Matches explicit `ecardPreset` or journey keyword rules on name/description.
 */
export function summaryMatchesJourney(summary, slug) {
  if (!summary) return false;
  const cfg = getJourneyBySlug(slug);
  if (!cfg || cfg.slug === "egift") return true;
  const preset = String(summary.ecardPreset || "").trim().toLowerCase();
  if (preset && preset === cfg.slug) return true;
  if (
    cfg.slug === "invitation" &&
    (preset === "weddinginvitation" || preset === "wedding-invitation")
  ) {
    return true;
  }
  const pseudo = {
    Tittle: summary.name || "",
    Description: summary.description || "",
    Category: "",
  };
  return cfg.match(pseudo);
}

const DRAFT_PREFIX = "giftekart_ecard_draft_";

export function draftStorageKey(productId) {
  return `${DRAFT_PREFIX}${productId}`;
}

export function saveEcardDraft(productId, data) {
  try {
    sessionStorage.setItem(draftStorageKey(productId), JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export function loadEcardDraft(productId) {
  try {
    const raw = sessionStorage.getItem(draftStorageKey(productId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearEcardDraft(productId) {
  try {
    sessionStorage.removeItem(draftStorageKey(productId));
  } catch {
    /* */
  }
}

const ACCOUNT_PREFIX = "giftekart_ecard_account_";

export function saveEcardAccount(productId, payload) {
  try {
    sessionStorage.setItem(`${ACCOUNT_PREFIX}${productId}`, JSON.stringify(payload));
  } catch {
    /* */
  }
}

export function loadEcardAccount(productId) {
  try {
    const raw = sessionStorage.getItem(`${ACCOUNT_PREFIX}${productId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearEcardAccount(productId) {
  try {
    sessionStorage.removeItem(`${ACCOUNT_PREFIX}${productId}`);
  } catch {
    /* */
  }
}

const SHARE_META_PREFIX = "giftekart_last_share_";

export function saveLastShare(productId, payload) {
  try {
    sessionStorage.setItem(
      `${SHARE_META_PREFIX}${productId}`,
      JSON.stringify(payload)
    );
  } catch {
    /* */
  }
}

export function loadLastShare(productId) {
  try {
    const raw = sessionStorage.getItem(`${SHARE_META_PREFIX}${productId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Simple demo coupons — replace with Cloud Function validation later. */
export const KNOWN_COUPONS = {
  WELCOME10: { type: "percent", value: 10, label: "10% off" },
  FIRST50: { type: "flat", value: 50, label: "₹50 off" },
};

export function applyCoupon(baseRupee, codeRaw) {
  const code = String(codeRaw || "")
    .trim()
    .toUpperCase();
  if (!code) return { total: baseRupee, discountLabel: null, discountAmount: 0 };
  const c = KNOWN_COUPONS[code];
  if (!c) return { total: baseRupee, discountLabel: null, discountAmount: 0, invalid: true };
  let discount = 0;
  if (c.type === "percent") {
    discount = Math.round((baseRupee * c.value) / 100);
  } else if (c.type === "flat") {
    discount = Math.min(c.value, Math.max(0, baseRupee - 1));
  }
  const total = Math.max(1, Math.round((baseRupee - discount) * 100) / 100);
  return { total, discountLabel: c.label, discountAmount: discount, code };
}
